import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './projectsList.css';

export const MAX_PROJECT_NAME = 128;

// Inline rename input (STRY0038867). Enter or blur saves, Esc cancels; an
// empty or unchanged name is a no-op. Events don't bubble to the row/card.
export const ProjectNameInput = ({ name, onSave, onCancel, className = '' }) => {
  const [value, setValue] = useState(name || '');
  const settled = useRef(false);

  const commit = () => {
    if (settled.current) return;
    settled.current = true;
    const next = value.trim();
    if (!next || next === name) onCancel();
    else onSave(next);
  };
  const cancel = () => {
    if (settled.current) return;
    settled.current = true;
    onCancel();
  };

  return (
    <input
      className={`pl-rename ${className}`.trim()}
      value={value}
      maxLength={MAX_PROJECT_NAME}
      aria-label="Project name"
      autoFocus
      onChange={e => setValue(e.target.value)}
      onFocus={e => e.target.select()}
      onClick={e => e.stopPropagation()}
      onDoubleClick={e => e.stopPropagation()}
      onKeyDown={e => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
      }}
      onBlur={commit}
    />
  );
};
ProjectNameInput.propTypes = {
  name: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  className: PropTypes.string
};

const PencilIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    aria-hidden="true"
  >
    <path d="M4 20h4L19 9l-4-4L4 16v4z" strokeLinejoin="round" />
    <path d="M14 6l4 4" />
  </svg>
);

// Project view header: the name plus a Rename button for users who may edit.
const ProjectNameEditor = ({ name, canEdit, onSave }) => {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <ProjectNameInput
        name={name}
        className="pl-rename--title"
        onCancel={() => setEditing(false)}
        onSave={async next => {
          setEditing(false);
          await onSave(next);
        }}
      />
    );
  }
  return (
    <>
      {name}
      {canEdit && (
        <button
          type="button"
          className="pl-iconbtn pl-rename-btn"
          title="Rename project"
          aria-label="Rename project"
          onClick={() => setEditing(true)}
        >
          <PencilIcon />
        </button>
      )}
    </>
  );
};
ProjectNameEditor.propTypes = {
  name: PropTypes.string,
  canEdit: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired
};

export default ProjectNameEditor;
